import { motion } from "framer-motion";
import { ArrowUpRight, Phone } from "lucide-react";
import { HERO } from "@/constants/testIds";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid={HERO.root}
      className="relative pt-28 md:pt-32 pb-0 border-b border-[#E5E5E5] overflow-hidden"
    >
      {/* Grid overlay */}
      <div className="absolute inset-0 vx-grid-overlay pointer-events-none" aria-hidden />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        {/* Overline */}
        <div className="flex items-center gap-3 mb-8">
          <span className="h-2 w-2 bg-[#FF5E00]" />
          <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#525252]">
            Toronto · Est. 1996 · TSSA Licensed
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-start">
          {/* Left: Massive headline */}
          <div className="lg:col-span-7">
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-black text-[46px] sm:text-[72px] lg:text-[104px] leading-[0.92] tracking-[-0.03em]"
            >
              VERTICAL
              <br />
              PRECISION.
              <br />
              <span className="text-[#FF5E00]">TORONTO</span> TOUGH.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mt-8 max-w-xl text-base lg:text-lg text-[#525252] leading-relaxed"
            >
              Nearly three decades engineering, modernizing and maintaining the
              elevators that keep Toronto and the GTA moving. From heritage
              walk-ups on Queen West to 60-storey towers on Bay Street — we
              build vertical transportation that lasts.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.5 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid={HERO.ctaQuote}
                onClick={() => scrollTo("contact")}
                className="group inline-flex items-center gap-3 bg-[#0A0A0A] hover:bg-[#FF5E00] text-white px-7 py-4 text-xs font-bold uppercase tracking-[0.24em] transition-colors"
              >
                Request a Quote
                <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>

              <a
                data-testid={HERO.ctaCall}
                href="tel:+14374940799"
                className="inline-flex items-center gap-3 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white px-7 py-4 text-xs font-bold uppercase tracking-[0.24em] transition-colors"
              >
                <Phone size={14} strokeWidth={2.5} />
                (437) 494-0799
              </a>
            </motion.div>
          </div>

          {/* Right: Image + spec block */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden border border-[#0A0A0A] vx-hard-shadow">
                <img
                  data-testid={HERO.image}
                  src="https://images.unsplash.com/photo-1758448656987-cfae6bf225e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwYnVpbGRpbmclMjBsb2JieXxlbnwwfHx8fDE3ODMxMjU1NDl8MA&ixlib=rb-4.1.0&q=85"
                  alt="Modern commercial lobby with marble reception and elevators in Toronto"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>

              {/* Spec chip */}
              <div className="hidden sm:block absolute -left-6 bottom-8 bg-white border border-[#0A0A0A] px-5 py-4 vx-hard-shadow">
                <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#525252]">
                  Certified By
                </div>
                <div className="mt-1 font-display font-black text-2xl">TSSA · CECA</div>
              </div>

              {/* Serial number */}
              <div className="absolute right-4 top-4 font-mono-lab text-[10px] tracking-widest text-white/90 bg-black/40 px-2 py-1">
                UNIT · 001 / GTA
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom ribbon */}
        <div className="mt-16 pt-6 border-t border-[#E5E5E5] flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
          <div className="flex flex-wrap items-center gap-x-4 sm:gap-x-6 gap-y-2 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] sm:tracking-[0.24em] text-[#525252]">
            <span>Commercial</span>
            <span className="h-1 w-1 bg-[#525252]" />
            <span>Residential</span>
            <span className="h-1 w-1 bg-[#525252]" />
            <span>Institutional</span>
          </div>
          <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.2em] sm:tracking-[0.24em] text-[#0A0A0A]">
            Scroll to explore ↓
          </div>
        </div>
      </div>
    </section>
  );
}
